import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";

import GetAppIcon from "@mui/icons-material/GetApp";
import BlurOnOutlinedIcon from "@mui/icons-material/BlurOnOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import React, { useEffect, useState } from "react";
import {
  addContactIdsToList,
  getFileForSystemId,
  getProcessedFile,
  removeContactIdsFromList,
} from "../../api/useractions";
import {
  acceptSuggestedChangesWithModifications,
  rollbackSuggestedChange,
  transitionSuggestedChange,
} from "../../api/suggestedchanges";

import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";

import { ChickenOutModal } from "../utils/ChickenOutModal";
import Modal from "@mui/material/Modal";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import IconButton from "@mui/material/IconButton";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import CardActions from "@mui/material/CardActions";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import PlaylistRemoveOutlinedIcon from "@mui/icons-material/PlaylistRemoveOutlined";
import { IntegratedSystem } from "../../api/integratedsystems";
import { isEmpty } from "lodash";
import DownloadIcon from "@mui/icons-material/Download";
import { DataProject } from "../../api/dataprojects";
import {
  AccountNameAutocomplete,
  ListNameAutocomplete,
} from "../utils/datasources/AutoComplete";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import { describeChangeCount } from "./utils";
import classes from "./css/suggestedchanges.module.css";

/*
 * Button weight on this toolbar reads as consequence, not as emphasis. Every button
 * here used to be `contained` + `primary`, which under the CIRROM variant painted six
 * actions of wildly different stakes the same amber - so nothing told a steward which
 * one reaches their CRM.
 *
 * Three tiers, and the tier is decided by what the click ends up doing - not by how it
 * gets there. Approve, Multi Approve and Revert all open a dialog first; that is a
 * safeguard on the way to a CRM write, not a lesser kind of action, so it does not drop
 * them a tier. Multi Approve is Approve over a set of records with a company chosen on
 * the way, and it is weighted the same.
 *
 * - `contained` - it writes to the customer's system of record. Approve, Multi Approve
 *   and Revert on the grid, and the confirm button inside each modal. The employment
 *   grid's company and all tabs carry two, because they genuinely offer two ways to
 *   commit; everywhere else there is a single filled control.
 * - `outlined` + amber - it decides something, but nothing leaves Cirrom. Reject.
 * - `outlined` + neutral hairline - utility. Details, Export, Add to list, Remove from
 *   list. Same box as the others so the row keeps one rhythm, but no amber: they neither
 *   write nor decide.
 */

/*
 * No styling constants live in this file. Geometry is in suggestedchanges.module.css
 * with the rest of this surface's styling; the neutral tier's colours are a button tier
 * in the theme (`MuiButton.styleOverrides.outlinedInherit`), where they resolve through
 * the palette and survive dark mode and the six non-CIRROM variants.
 *
 * Buttons carry no margin of their own either - the row's `gap` owns the spacing, so the
 * first button starts on the same edge as the tabs, the chart cards, the filter and the
 * grid.
 */

type BulkReportVariant = "success" | "warning" | "error";
type BulkReporter = (message: string, variant: BulkReportVariant) => void;

/**
 * Wording for the two outcomes of one bulk action.
 *
 * Every action on this toolbar is reported as all-or-nothing. Approve and reject are
 * genuinely atomic - one request for the whole selection, no per-change detail in the
 * response. Revert and company association do send one request per change and could in
 * principle land in between, but reading that split is deliberately left out for now:
 * a run that does not fully succeed reports failure and removes no rows, so a change
 * that is still pending is never dropped from the queue.
 *
 * Counts land on both ends of these sentences and a queue of one is an ordinary day
 * here, so singular and plural are both written out rather than left to a trailing
 * "(s)". "None of the 1 changes reached your CRM" is the kind of line that makes a
 * steward doubt the number it is reporting.
 */
type BulkActionCopy = {
  succeeded(total: number): string;
  failed(total: number): string;
};

const APPROVE_COPY: BulkActionCopy = {
  succeeded: (total) => `${describeChangeCount(total)} written to your CRM.`,
  failed: (total) =>
    total === 1
      ? "The change did not reach your CRM. It is still pending - try again."
      : `None of the ${total} changes reached your CRM. They are still pending - try again.`,
};

const REJECT_COPY: BulkActionCopy = {
  succeeded: (total) =>
    `${describeChangeCount(total)} rejected. Cirrom won't suggest ${
      total === 1 ? "it" : "them"
    } again.`,
  failed: (total) =>
    total === 1
      ? "The change could not be rejected. It is still pending - try again."
      : `None of the ${total} changes could be rejected. They are still pending - try again.`,
};

const REVERT_COPY: BulkActionCopy = {
  succeeded: (total) => `${describeChangeCount(total)} reverted.`,
  failed: (total) =>
    total === 1
      ? "The change could not be reverted. Refresh the grid to see where it stands."
      : `Those ${total} changes could not be reverted. Refresh the grid to see where they stand.`,
};

/**
 * useBulkActionReport
 *
 * Success dismisses itself; anything less does not.
 *
 * A partial or refused CRM write is the one thing on this surface a steward must not
 * miss, and a toast that clears itself after a few seconds is exactly how they would
 * miss it - so warnings and errors persist until they are closed. Same rule the
 * record modal applies to its own banner.
 */
const useBulkActionReport = (): BulkReporter => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  return (message: string, variant: BulkReportVariant) => {
    enqueueSnackbar(message, {
      variant,
      persist: variant !== "success",
      action:
        variant === "success"
          ? undefined
          : (key) => (
              <Button
                color="inherit"
                size="small"
                onClick={() => closeSnackbar(key)}
              >
                Dismiss
              </Button>
            ),
    });
  };
};

/**
 * runWriteBack Function
 *
 * Runs one bulk action over the selected rows and reports how it went.
 *
 * Three rules hold here, and they are the reason this wrapper exists:
 *
 * - The loading overlay is cleared in a `finally`. A request that threw used to leave
 *   it spinning for the rest of the session with nothing on screen to explain it.
 * - A run that did not succeed removes no rows and keeps the selection, so nothing that
 *   might still be pending disappears from the queue, and a retry costs a click rather
 *   than rebuilding the selection.
 * - The outcome is always reported. Every one of these actions used to swallow its
 *   error into `console.log`, so a failed run and a clean one looked identical.
 *
 * @param {any} gridApi - The grid API for interacting with the grid.
 * @param {function} report - Surfaces the outcome to the steward.
 * @param {BulkActionCopy} copy - Wording for the two outcomes.
 * @param {number} selectedCount - Count named in the failure message, matching the one the button showed.
 * @param {function} apiCall - Runs the action for every selected id.
 * @param {function} [onGridActionComplete] - Called with the ids that were actioned.
 */
const runWriteBack = async (
  gridApi: any,
  report: BulkReporter,
  copy: BulkActionCopy,
  selectedCount: number,
  apiCall: (nodeIds: number[]) => Promise<any>,
  onGridActionComplete?: (nodeIds: number[]) => void
) => {
  const nodeIds: number[] = gridApi
    .getSelectedNodes()
    .map((node) => node.data?.id)
    .filter((id) => id !== undefined && id !== null);

  if (!nodeIds.length) {
    return false;
  }

  gridApi.setGridOption("loading", true);
  try {
    await apiCall(nodeIds);
    gridApi.deselectAll();
    if (onGridActionComplete !== undefined) {
      onGridActionComplete(nodeIds);
    }
    report(copy.succeeded(nodeIds.length), "success");
    return true;
  } catch (err) {
    console.error("Bulk write-back failed", err);
    report(copy.failed(selectedCount), "error");
    return false;
  } finally {
    gridApi.setGridOption("loading", false);
  }
};

/**
 * AddToMarketingCampaignButton Component
 *
 * This component provides a button that opens a modal to add selected items to a marketing list.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {function} [props.onGridActionComplete] - Callback triggered when the grid action is complete.
 * @param {IntegratedSystem|DataProject} props.integratedSystem - Integrated system or data project information.
 * @returns {JSX.Element} - Rendered component.
 */

export const AddToMarketingCampaignButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    onGridActionComplete?(): void;
    integratedSystem: IntegratedSystem | DataProject;
  }>
) => {
  const { rowsSelected, gridApi, integratedSystem } = props;
  const report = useBulkActionReport();
  // State for controlling modal visibility and marketing list name
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [marketingListName, setMarketingListName] = useState<string>("");
  const [listType, setListType] = useState<"new" | "existing">("new");
  const [selectedExistingListId, setSelectedExistingListId] =
    useState<string>(null);

  const resetForm = () => {
    setMarketingListName("");
    setListType("new");
    setSelectedExistingListId(null);
  };

  const closeModal = () => {
    resetForm();
    setModalOpen(false);
  };

  const handleAddToList = () => {
    const record_ids = [];
    // Collect CRM contact IDs of selected nodes
    gridApi.getSelectedNodes().forEach((node) => {
      if (node.data.crm_contact_id) {
        record_ids.push(node.data.crm_contact_id);
      }
    });

    const systemId = integratedSystem.hasOwnProperty("integrated_system_id")
      ? //@ts-ignore
        integratedSystem.integrated_system_id
      : integratedSystem.id;

    // Use either the entered new list name or the selected existing list name
    const listName =
      listType === "new" ? marketingListName : selectedExistingListId;

    // Call API to add contact IDs to the marketing list
    addContactIdsToList(systemId, record_ids, listName)
      .then(() => {
        report(
          `${record_ids.length} ${
            record_ids.length === 1 ? "contact" : "contacts"
          } added to the marketing list.`,
          "success"
        );
        closeModal();
      })
      .catch((err) => {
        // The modal stays open on failure: the list name is still in the form, so a
        // retry costs a click rather than re-entering it.
        console.error("Add to marketing list failed", err);
        report(
          "Those contacts could not be added to the list. Nothing has changed - try again.",
          "error"
        );
      });
  };

  return (
    <>
      {/* Modal for adding to marketing list */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm" style={{ padding: 0 }}>
          <Card style={{ padding: "16px" }}>
            <CardHeader
              title={"Add to marketing list"}
              titleTypographyProps={{ variant: "h3" }}
              action={
                <IconButton onClick={closeModal}>
                  <CloseOutlinedIcon />
                </IconButton>
              }
            />
            <Divider />
            <CardContent>
              {/* Radio button group for list type selection */}
              <div style={{ marginBottom: "16px" }}>
                <Button
                  variant={listType === "new" ? "contained" : "outlined"}
                  color="primary"
                  size="small"
                  style={{ marginRight: "8px" }}
                  onClick={() => setListType("new")}
                >
                  Create new list
                </Button>
                <Button
                  variant={listType === "existing" ? "contained" : "outlined"}
                  color="primary"
                  size="small"
                  onClick={() => setListType("existing")}
                >
                  Add to existing list
                </Button>
              </div>

              {/* Input for new marketing list name or autocomplete for existing lists */}
              {listType === "new" ? (
                <TextField
                  data-testid={"new-marketing-list"}
                  required
                  fullWidth
                  variant="outlined"
                  size="small"
                  margin="dense"
                  label={"New marketing list name"}
                  value={marketingListName}
                  onChange={(event) => {
                    setMarketingListName(event.target.value);
                  }}
                />
              ) : (
                <div style={{ marginTop: "8px", marginBottom: "8px" }}>
                  <Typography variant={"h6"}>Select existing list:</Typography>

                  <ListNameAutocomplete
                    integratedSystemId={
                      integratedSystem.hasOwnProperty("integrated_system_id")
                        ? //@ts-ignore
                          integratedSystem.integrated_system_id
                        : integratedSystem.id
                    }
                    onSelectionChanged={(listId) =>
                      setSelectedExistingListId(listId)
                    }
                  />
                </div>
              )}
              <Divider style={{ marginTop: "16px" }} />
            </CardContent>
            <CardActions
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              {/* Button to add selected items to marketing list */}
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={classes.toolbarButton}
                disabled={
                  (listType === "new" && !marketingListName) ||
                  (listType === "existing" && !selectedExistingListId)
                }
                onClick={handleAddToList}
              >
                Add to list
              </Button>
            </CardActions>
          </Card>
        </Container>
      </Modal>
      {/* Button to open the modal */}
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<PlaylistAddCheckOutlinedIcon />}
        size="small"
        className={classes.toolbarButtonBordered}
        onClick={() => setModalOpen(true)}
        disabled={rowsSelected === 0}
      >
        Add to list
      </Button>
    </>
  );
};

/**
 * RemoveFromListButton Component
 *
 * This component provides a button that opens a modal to remove the selected contacts
 * from an existing list in the CRM.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {IntegratedSystem|DataProject} props.integratedSystem - Integrated system or data project information.
 * @returns {JSX.Element} - Rendered component.
 */
export const RemoveFromListButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    integratedSystem: IntegratedSystem | DataProject;
  }>
) => {
  const { rowsSelected, gridApi, integratedSystem } = props;
  const report = useBulkActionReport();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedListId, setSelectedListId] = useState<string>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const systemId = integratedSystem.hasOwnProperty("integrated_system_id")
    ? //@ts-ignore
      integratedSystem.integrated_system_id
    : integratedSystem.id;

  const closeModal = () => {
    setSelectedListId(null);
    setModalOpen(false);
  };

  const handleRemoveFromList = () => {
    const record_ids = [];
    gridApi.getSelectedNodes().forEach((node) => {
      if (node.data.crm_contact_id) {
        record_ids.push(node.data.crm_contact_id);
      }
    });

    setLoading(true);
    removeContactIdsFromList(systemId, record_ids, selectedListId)
      .then(() => {
        report(
          `${record_ids.length} ${
            record_ids.length === 1 ? "contact" : "contacts"
          } removed from the list.`,
          "success"
        );
        closeModal();
      })
      .catch((err) => {
        // The CRM removes contacts one at a time, so a failed run may have taken some
        // off already. The modal stays open with the list still chosen for a retry.
        console.error("Remove from list failed", err);
        report(
          "Those contacts could not all be removed from the list. Check the list in your CRM before trying again.",
          "error"
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      {/* Modal for removing from a list */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm" style={{ padding: 0 }}>
          <Card style={{ padding: "16px" }}>
            <CardHeader
              title={"Remove from list"}
              titleTypographyProps={{ variant: "h3" }}
              action={
                <IconButton onClick={closeModal}>
                  <CloseOutlinedIcon />
                </IconButton>
              }
            />
            <Divider />
            <CardContent>
              <div style={{ marginTop: "8px", marginBottom: "8px" }}>
                <Typography variant={"h6"}>
                  Remove the selected contacts ({rowsSelected}) from:
                </Typography>

                <ListNameAutocomplete
                  integratedSystemId={systemId}
                  onSelectionChanged={(listId) => setSelectedListId(listId)}
                />
              </div>
              <Divider style={{ marginTop: "16px" }} />
            </CardContent>
            <CardActions
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={classes.toolbarButton}
                disabled={!selectedListId || loading}
                onClick={handleRemoveFromList}
              >
                {loading ? "Processing..." : "Remove from list"}
              </Button>
            </CardActions>
          </Card>
        </Container>
      </Modal>
      {/* Button to open the modal */}
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<PlaylistRemoveOutlinedIcon />}
        size="small"
        className={classes.toolbarButtonBordered}
        onClick={() => setModalOpen(true)}
        disabled={rowsSelected === 0}
      >
        Remove from list
      </Button>
    </>
  );
};

/**
 * MultiCompanyAssociationButton Component
 *
 * This component provides a button that opens a modal to bulk associate multiple contacts with a company.
 * It's specifically for use in the Employment Changes grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {function} [props.onGridActionComplete] - Callback triggered when the grid action is complete.
 * @param {IntegratedSystem|DataProject} props.integratedSystem - Integrated system or data project information.
 * @returns {JSX.Element} - Rendered component.
 */
export const MultiCompanyAssociationButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    onGridActionComplete?(nodeIds: number[]): void;
    integratedSystem: IntegratedSystem | DataProject;
  }>
) => {
  const { rowsSelected, gridApi, integratedSystem, onGridActionComplete } =
    props;
  const report = useBulkActionReport();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] =
    useState<{ name: string; crm_account_id: string }>();
  const [loading, setLoading] = useState<boolean>(false);

  const systemId = integratedSystem.hasOwnProperty("integrated_system_id")
    ? //@ts-ignore
      integratedSystem.integrated_system_id
    : integratedSystem.id;

  const resetForm = () => {
    setSelectedCompany(undefined);
  };

  const closeModal = () => {
    resetForm();
    setModalOpen(false);
  };

  const associateCopy: BulkActionCopy = {
    succeeded: (total) =>
      `${describeChangeCount(total)} written to your CRM against ${
        selectedCompany?.name
      }.`,
    failed: (total) =>
      total === 1
        ? "The change could not be associated. Refresh the grid to see where it stands."
        : `Those ${total} changes could not be associated. Refresh the grid to see where they stand.`,
  };

  /**
   * Associate every selected contact with the chosen company.
   *
   * The old sequential loop aborted on the first refusal, leaving the earlier contacts
   * written, the rest untouched, and the steward told neither. The modal stays open on
   * failure, so the chosen company does not have to be re-picked before retrying.
   */
  const handleAssociate = () => {
    if (!selectedCompany) return;

    setLoading(true);

    runWriteBack(
      gridApi,
      report,
      associateCopy,
      rowsSelected,
      (nodeIds) =>
        Promise.all(
          nodeIds.map((nodeId) =>
            acceptSuggestedChangesWithModifications(
              nodeId,
              selectedCompany.name,
              selectedCompany.crm_account_id
            )
          )
        ),
      onGridActionComplete
    )
      .then((succeeded) => {
        if (succeeded) {
          closeModal();
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      {/* Modal for associating contacts with company */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm" style={{ padding: 0 }}>
          <Card style={{ padding: "16px" }}>
            <CardHeader
              title={"Associate with Company"}
              titleTypographyProps={{ variant: "h3" }}
              action={
                <IconButton onClick={closeModal}>
                  <CloseOutlinedIcon />
                </IconButton>
              }
            />
            <Divider />
            <CardContent>
              <Typography variant="body1" style={{ marginBottom: "16px" }}>
                Select the company to associate with the selected contacts (
                {rowsSelected}).
              </Typography>

              <div style={{ marginTop: "16px", marginBottom: "16px" }}>
                <Typography variant={"h6"}>Company:</Typography>
                <AccountNameAutocomplete
                  integratedSystemId={systemId}
                  onSelectionChanged={setSelectedCompany}
                />
              </div>

              <Divider style={{ marginTop: "16px" }} />
            </CardContent>
            <CardActions
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={classes.toolbarButton}
                disabled={!selectedCompany || loading}
                onClick={handleAssociate}
              >
                {loading ? "Processing..." : "Associate Company"}
              </Button>
            </CardActions>
          </Card>
        </Container>
      </Modal>

      {/* Button to open the modal */}
      <Button
        variant="contained"
        color="primary"
        startIcon={<BusinessIcon />}
        size="small"
        className={classes.toolbarButton}
        onClick={() => setModalOpen(true)}
        disabled={rowsSelected === 0}
      >
        Multi Approve
      </Button>
    </>
  );
};

/**
 * ApproveChangesButton Component
 *
 * This component provides a button to approve selected suggested changes in the grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {function} [props.onGridActionComplete] - Callback triggered when the grid action is complete.
 * @param {string} [props.destinationName] - The system the approved changes are written to, named in the confirmation.
 * @returns {JSX.Element} - Rendered component.
 */
const ApproveChangesButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    onGridActionComplete?(nodeIds: number[]): void;
    destinationName?: string;
  }>
) => {
  const { gridApi, rowsSelected, onGridActionComplete, destinationName } =
    props;
  const report = useBulkActionReport();
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  /**
   * Disable approve for INFORMATION tickets (they can only be rejected)
   */
  const hasInformationSelected =
    gridApi
      ?.getSelectedNodes?.()
      ?.some((node) => node.data?.change_type === "INFORMATION") ?? false;

  const isDisabled = rowsSelected === 0 || hasInformationSelected;

  /**
   * Approve suggested changes for selected nodes. Only reachable once the
   * confirmation has been answered - nothing reaches the CRM on the button press.
   */
  const approveSuggestedChanges = () => {
    void runWriteBack(
      gridApi,
      report,
      APPROVE_COPY,
      rowsSelected,
      (nodeIds) => transitionSuggestedChange(nodeIds),
      onGridActionComplete
    );
  };

  return (
    <>
      <Tooltip
        title={
          hasInformationSelected ? "Cannot approve Information tickets" : ""
        }
      >
        <span style={{ display: "inline-flex" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DoneAllOutlinedIcon />}
            size="small"
            className={classes.toolbarButton}
            onClick={() => setConfirmOpen(true)}
            disabled={isDisabled}
          >
            {`Approve(${rowsSelected})`}
          </Button>
        </span>
      </Tooltip>
      {/*
        Approve is the one action on this toolbar that writes into the customer's
        system of record, and the header checkbox can select every row matching the
        current filter in a single click. So the confirmation names the count and the
        destination rather than saying "all" - the size of what is being committed to
        should not have to be inferred from the button the steward just left.
      */}

      <ChickenOutModal
        modalOpen={confirmOpen}
        title={`Write ${describeChangeCount(rowsSelected)} to ${
          destinationName || "your CRM"
        }?`}
        message={"Cirrom will apply these changes to the matching records."}
        confirmLabel={"Write to CRM"}
        onModalOpenChange={setConfirmOpen}
        onSuccess={approveSuggestedChanges}
      />
    </>
  );
};

/**
 * RevertChangesButton Component
 *
 * This component provides a button to revert selected suggested changes in the grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {function} [props.onGridActionComplete] - Callback triggered when the grid action is complete.
 * @returns {JSX.Element} - Rendered component.
 */
const RevertChangesButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    onGridActionComplete?(nodeIds: number[]): void;
  }>
) => {
  const { gridApi, rowsSelected, onGridActionComplete } = props;
  const report = useBulkActionReport();

  /**
   * Revert suggested changes for selected nodes.
   *
   * Rollback is one call per change. The run used to report nothing either way - no
   * catch at all - so a refused rollback left the overlay spinning and the steward with
   * no idea the batch had not gone through.
   */
  const revertSuggestedChanges = () => {
    void runWriteBack(
      gridApi,
      report,
      REVERT_COPY,
      rowsSelected,
      (nodeIds) =>
        Promise.all(nodeIds.map((nodeId) => rollbackSuggestedChange(nodeId))),
      onGridActionComplete
    );
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={<ReplayOutlinedIcon />}
      size="small"
      className={classes.toolbarButton}
      onClick={revertSuggestedChanges}
      disabled={rowsSelected === 0}
    >
      Revert
    </Button>
  );
};

/**
 * RejectChangesButton Component
 *
 * This component provides a button to reject selected suggested changes in the grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {function} [props.onGridActionComplete] - Callback triggered when the grid action is complete.
 * @returns {JSX.Element} - Rendered component.
 */
const RejectChangesButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    onGridActionComplete?(nodeIds): void;
  }>
) => {
  const { gridApi, rowsSelected, onGridActionComplete } = props;
  const report = useBulkActionReport();

  /**
   * Reject suggested changes for selected nodes.
   */
  const rejectSuggestedChanges = () => {
    void runWriteBack(
      gridApi,
      report,
      REJECT_COPY,
      rowsSelected,
      (nodeIds) => transitionSuggestedChange(nodeIds, true),
      onGridActionComplete
    );
  };

  const [chickenOutOpen, setChickenOutOpen] = useState<boolean>(false);
  return (
    <>
      {/* Reject button */}
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ClearOutlinedIcon />}
        size="small"
        className={classes.toolbarButtonBordered}
        onClick={() => setChickenOutOpen(true)}
        disabled={rowsSelected === 0}
      >
        {`Reject(${rowsSelected})`}
      </Button>
      {/* Chicken Out Modal */}

      <ChickenOutModal
        modalOpen={chickenOutOpen}
        title={`Reject ${describeChangeCount(rowsSelected)}?`}
        message={`Cirrom won't suggest ${
          rowsSelected === 1 ? "it" : "them"
        } again. Nothing is written to your CRM.`}
        confirmLabel={"Reject"}
        onModalOpenChange={setChickenOutOpen}
        onSuccess={rejectSuggestedChanges}
      />
    </>
  );
};

/**
 * DetailsButton Component
 *
 * This component provides a button to view details of a selected row in the grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @returns {JSX.Element} - Rendered component.
 */
export const DetailsButton = (
  props: Readonly<{ gridApi: any; rowsSelected: number }>
) => {
  const { gridApi, rowsSelected } = props;

  /**
   * Handle button click to view details of a selected row.
   */
  const handleButtonClick = () => {
    if (gridApi.getSelectedNodes().length === 1) {
      gridApi.dispatchEvent({
        type: "rowDoubleClicked",
        node: gridApi.getSelectedNodes()[0],
      });
    }
  };

  return (
    <Button
      variant="outlined"
      color="inherit"
      startIcon={<BlurOnOutlinedIcon />}
      size="small"
      className={classes.toolbarButtonBordered}
      disabled={rowsSelected !== 1}
      onClick={handleButtonClick}
    >
      Details
    </Button>
  );
};

/**
 * ExportButton Component
 *
 * This component provides a button to initiate the export of a processed file.
 *
 * @param {object} props - Component props.
 * @param {number} props.fileId - The ID of the file to be exported.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @returns {JSX.Element} - Rendered component.
 */
const ExportButton = (props: Readonly<{ fileId: number; gridApi: any }>) => {
  const { fileId, gridApi } = props;
  const report = useBulkActionReport();

  /**
   * Initiates the export of the processed file.
   * Shows loading overlay while exporting; clears it in a `finally` so a failed
   * export cannot leave the grid looking busy with no download and no message.
   */
  const exportFile = () => {
    gridApi.setGridOption("loading", true);
    getProcessedFile(fileId)
      .catch((err) => {
        console.error("Export failed", err);
        report(
          "The export could not be prepared. Nothing has been downloaded - try again.",
          "error"
        );
      })
      .finally(() => gridApi.setGridOption("loading", false));
  };

  return (
    <Button
      variant="outlined"
      color="inherit"
      startIcon={<GetAppIcon />}
      size="small"
      className={classes.toolbarButtonBordered}
      onClick={exportFile}
    >
      Export
    </Button>
  );
};

/**
 * SuggestedChangesRevertToolbar Component
 *
 * This component represents a toolbar for reverting suggested changes in a grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {function} [props.onGridActionComplete] - Callback function to be executed after grid action.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @returns {JSX.Element} - Rendered component.
 */
export const SuggestedChangesRevertToolbar = (
  props: Readonly<{
    gridApi: any;
    onGridActionComplete?(): void;
    rowsSelected: number;
    timeframe?: string;
    onTimeframeChange?: (timeframe: string) => void;
  }>
) => {
  const {
    gridApi,
    rowsSelected,
    onGridActionComplete,
    timeframe,
    onTimeframeChange,
  } = props;

  const timeframeOptions = [
    { name: "Last 7 Days", value: "last_7_days" },
    { name: "Last 30 Days", value: "last_30_days" },
    { name: "All Time", value: null },
  ];

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      {onTimeframeChange && (
        <ButtonGroup size="small" color="primary" style={{ margin: "2px" }}>
          {timeframeOptions.map((option) => (
            <Button
              key={option.name}
              variant={timeframe === option.value ? "contained" : "outlined"}
              onClick={() => onTimeframeChange(option.value)}
              style={{ padding: "0em 0.5em" }}
            >
              {option.name}
            </Button>
          ))}
        </ButtonGroup>
      )}

      {/* DetailsButton component */}
      <DetailsButton rowsSelected={rowsSelected} gridApi={gridApi} />

      {/* RevertChangesButton component */}
      <RevertChangesButton
        gridApi={gridApi}
        rowsSelected={rowsSelected}
        onGridActionComplete={onGridActionComplete}
      />
    </div>
  );
};

/**
 * SuggestedChangesButtonsToolbar Component
 *
 * This component represents a toolbar for various actions related to suggested changes in a grid.
 *
 * @param {object} props - Component props.
 * @param {number} [props.fileId] - The ID of the file associated with the grid.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {function} [props.onGridActionComplete] - Callback function to be executed after grid action.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {IntegratedSystem | DataProject} [props.integratedSystem] - Integrated system or data project information.
 * @returns {JSX.Element} - Rendered component.
 */
export const SuggestedChangesButtonsToolbar = (
  props: Readonly<{
    fileId?: number;
    gridApi: any;
    onGridActionComplete?(nodeIds: number[]): void;
    rowsSelected: number;
    integratedSystem?: IntegratedSystem | DataProject;
    dataPoint?: string;
    currentTab?: string;
    timeframe?: string | null;
    onTimeframeChange?: (tf: string | null) => void;
  }>
) => {
  const {
    fileId,
    gridApi,
    rowsSelected,
    onGridActionComplete,
    integratedSystem,
    dataPoint,
    currentTab,
    timeframe,
    onTimeframeChange,
  } = props;

  const [localFileId, setLocalFileId] = useState<number>(undefined);

  useEffect(() => {
    // Fetch the file associated with the integrated system if not provided

    if (
      !fileId &&
      integratedSystem &&
      "type" in integratedSystem &&
      integratedSystem?.type?.indexOf("CRM") === -1
    ) {
      getFileForSystemId(integratedSystem.id)
        .then((fileResponse) => {
          if (fileResponse) {
            setLocalFileId(fileResponse.id);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [fileId, integratedSystem]);

  const timeframeOptions = [
    { name: "Last 7 Days", value: "last_7_days" },
    { name: "Last 30 Days", value: "last_30_days" },
    { name: "All Time", value: null },
  ];

  // Determine if we're in the Employment Changes grid
  const isEmploymentGrid = dataPoint === "EMPLOYMENT";
  // For company tab specifically we add the associate company button
  const isCompanyTab =
    isEmploymentGrid && (currentTab === "company" || currentTab === "all");

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      {onTimeframeChange && (
        <ButtonGroup size="small" color="primary" style={{ margin: "2px" }}>
          {timeframeOptions.map((option) => (
            <Button
              key={option.name}
              variant={timeframe === option.value ? "contained" : "outlined"}
              onClick={() => onTimeframeChange(option.value)}
              style={{ padding: "0em 0.5em" }}
            >
              {option.name}
            </Button>
          ))}
        </ButtonGroup>
      )}
      {/* DetailsButton component */}
      <DetailsButton rowsSelected={rowsSelected} gridApi={gridApi} />

      {/* ApproveChangesButton component */}
      <ApproveChangesButton
        gridApi={gridApi}
        rowsSelected={rowsSelected}
        onGridActionComplete={onGridActionComplete}
        destinationName={integratedSystem?.name}
      />
      {/* RejectChangesButton component */}
      <RejectChangesButton
        gridApi={gridApi}
        rowsSelected={rowsSelected}
        onGridActionComplete={onGridActionComplete}
      />
      {/* Conditionally render ExportButton based on file and integrated system */}
      {fileId ||
      (integratedSystem && integratedSystem.system_type === "EXCEL") ? (
        <ExportButton fileId={fileId || localFileId} gridApi={gridApi} />
      ) : null}
      {integratedSystem && integratedSystem.system_type === "INTERACTION" && (
        <>
          <AddToMarketingCampaignButton
            gridApi={gridApi}
            rowsSelected={rowsSelected}
            integratedSystem={integratedSystem}
          />
          <RemoveFromListButton
            gridApi={gridApi}
            rowsSelected={rowsSelected}
            integratedSystem={integratedSystem}
          />
        </>
      )}
      {/* Show Company Association Button for Employment Changes */}
      {isCompanyTab && integratedSystem && (
        <MultiCompanyAssociationButton
          gridApi={gridApi}
          rowsSelected={rowsSelected}
          onGridActionComplete={onGridActionComplete}
          integratedSystem={integratedSystem}
        />
      )}
    </div>
  );
};

/**
 * RuleEvaluationButtonsToolbar Component
 *
 * This component represents a toolbar for various actions related to rule evaluation in a grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {function} [props.onGridActionComplete] - Callback function to be executed after grid action.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {IntegratedSystem} [props.integratedSystem] - Integrated system information.
 * @returns {JSX.Element} - Rendered component.
 */
export const RuleEvaluationButtonsToolbar = (
  props: Readonly<{
    gridApi: any;
    onGridActionComplete?(nodeIds): void;
    rowsSelected: number;
    integratedSystem?: IntegratedSystem;
  }>
) => {
  const { gridApi, rowsSelected, onGridActionComplete, integratedSystem } =
    props;

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      {/* DetailsButton component */}
      <DetailsButton rowsSelected={rowsSelected} gridApi={gridApi} />
      {/* RejectChangesButton component */}
      <RejectChangesButton
        gridApi={gridApi}
        rowsSelected={rowsSelected}
        onGridActionComplete={onGridActionComplete}
      />

      {integratedSystem && integratedSystem.system_type === "INTERACTION" && (
        <>
          <AddToMarketingCampaignButton
            gridApi={gridApi}
            rowsSelected={rowsSelected}
            integratedSystem={integratedSystem}
          />
          <RemoveFromListButton
            gridApi={gridApi}
            rowsSelected={rowsSelected}
            integratedSystem={integratedSystem}
          />
        </>
      )}
      {/* Conditionally render AddToMarketingCampaignButton for INTERACTION system */}
      {!isEmpty(gridApi) && (
        <IconButton
          color={"info"}
          component={"span"}
          onClick={() => {
            gridApi.exportDataAsCsv();
          }}
          style={{ height: "20px", width: "20px" }}
        >
          <DownloadIcon />
        </IconButton>
      )}
    </div>
  );
};
